import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isOnlinePaymentsEnabled } from '../../../utils/onlinePayments';
import { PAYMENT_LOGO_SRC, PaymentLogoImage } from './PaymentMethodLogos';

export type OnlinePaymentGateway = 'esewa' | 'khalti' | 'connectips' | 'fonepay';

interface PaymentMethodOption {
  id: OnlinePaymentGateway;
  label: string;
  logoSrc: string;
}

const PAYMENT_METHODS: PaymentMethodOption[] = [
  { id: 'esewa', label: 'eSewa', logoSrc: PAYMENT_LOGO_SRC.esewa },
  { id: 'khalti', label: 'Khalti', logoSrc: PAYMENT_LOGO_SRC.khalti },
  { id: 'connectips', label: 'connectIPS', logoSrc: PAYMENT_LOGO_SRC.connectips },
  { id: 'fonepay', label: 'Fonepay QR', logoSrc: PAYMENT_LOGO_SRC.fonepay },
];

export interface PaymentMethodPickerProps {
  loadingGateway: OnlinePaymentGateway | null;
  disabled?: boolean;
  onSelect: (gateway: OnlinePaymentGateway) => void;
}

function LogoTile({
  label,
  logoSrc,
  onClick,
  disabled,
  loading,
}: {
  label: string;
  logoSrc: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const content = (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        width: 96,
        height: 56,
        borderRadius: 2,
        border: '1px solid',
        borderColor: loading ? 'primary.main' : 'divider',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1,
        opacity: disabled && !loading ? 0.45 : 1,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
        boxShadow: loading ? 2 : 0,
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          borderColor: disabled ? 'divider' : 'primary.main',
          boxShadow: disabled ? 0 : 2,
          transform: disabled ? 'none' : 'translateY(-1px)',
        },
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.75)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <PaymentLogoImage src={logoSrc} alt={label} height={28} />
    </Paper>
  );

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-label={`Pay with ${label}`}
      sx={{ borderRadius: 2 }}
    >
      {content}
    </ButtonBase>
  );
}

export default function PaymentMethodPicker({
  loadingGateway,
  disabled = false,
  onSelect,
}: PaymentMethodPickerProps) {
  const isBusy = disabled || Boolean(loadingGateway);

  if (!isOnlinePaymentsEnabled()) {
    return (
      <Alert severity="info">
        Online payment is coming soon. Please confirm and pay at The Sports Hub venue for now.
      </Alert>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
        Choose payment method
      </Typography>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {PAYMENT_METHODS.map((method) => (
          <LogoTile
            key={method.id}
            label={method.label}
            logoSrc={method.logoSrc}
            loading={loadingGateway === method.id}
            disabled={isBusy}
            onClick={() => onSelect(method.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
}
